import { get } from 'lodash';
import { useTranslation } from 'react-i18next';
import toast from 'react-hot-toast';

import { Hooks } from 'modules/process';
import { useRegisterRef } from 'modules/registry/hooks';
import React, { useMemo, useState } from 'react';
import * as ProcessModule from 'modules/process';
import Button from 'components/Button';
import * as Icon from 'components/Icon';
import * as Fields from 'components/Fields';

import Payment from './Payment';
import Spinner from 'components/Spinner';
import { useHistory } from 'react-router';
import Modal from 'components/Modal';
import { SendReport } from 'modules/certificate/forms';

import classes from '../CertificateVew.module.scss';

interface Props {
  suspensionApplicationId: number;
  registerId: number;
  licenseId: string;
  feeDateEnd: string;
}
const DynamicProcessButtons = ({ suspensionApplicationId, registerId, licenseId, feeDateEnd }: Props) => {
  const { t, i18n } = useTranslation();
  const history = useHistory();
  const { item, isFetched } = useRegisterRef({ registerId });
  const Process = Hooks.useProcessStart({ registerId });
  const [isLoading, setLoading] = useState<string | null>(null);
  const PROCESS_TYPES = ProcessModule.Constants.TYPE;
  const [isLoadingBranch, setBranchLoading] = useState(false);

  function onError(error: any) {
    const message = get(error, `message.title[${i18n.language}]`) || '';
    const type = get(error, `message.type`) || '';
    !!message && (type === 'INFO' ? toast(message) : toast.error(message));
  }

  const onClick = (type: ProcessModule.Constants.TYPE) => {
    if (!isLoading) {
      setLoading(type);
      Process.start(type, {
        onError,
        onFinally: () => {
          setLoading(null);
        }
      });
    }
  };

  const onClickBranch = () => {
    if (!isLoadingBranch) {
      setBranchLoading(true);
      Process.branch(PROCESS_TYPES.REGISTRATION, {
        onError,
        onFinally: () => setBranchLoading(false)
      });
    }
  };

  const [showSendReportModal, setShowSendReportModal] = useState(false);

  const onClickReports = () => {
    setShowSendReportModal(true);
  };

  const renderButtons = useMemo(
    () => [
      {
        key: PROCESS_TYPES.SUSPEND_TO_ACTIVE,
        button: !!suspensionApplicationId ? (
          <Button
            className="m-r-12"
            prefixIcon={<Icon.System.Mail />}
            onClick={() => history.push(`/application/${suspensionApplicationId}`)}
          >
            {t('send_information_about_the_elimination_of_defects')}
          </Button>
        ) : (
          <Button
            className="m-r-12"
            variant="green"
            prefixIcon={<Icon.System.Check />}
            disabled={isLoading === PROCESS_TYPES.SUSPEND_TO_ACTIVE}
            isLoading={isLoading === PROCESS_TYPES.SUSPEND_TO_ACTIVE}
            onClick={() => onClick(PROCESS_TYPES.SUSPEND_TO_ACTIVE)}
          >
            {t('suspend_to_active')}
          </Button>
        )
      },
      {
        key: PROCESS_TYPES.CHANGE,
        button: (
          <Button
            className="m-r-12"
            prefixIcon={<Icon.System.Edit />}
            disabled={isLoading === PROCESS_TYPES.CHANGE}
            isLoading={isLoading === PROCESS_TYPES.CHANGE}
            onClick={() => onClick(PROCESS_TYPES.CHANGE)}
          >
            {t('Renewal')}
          </Button>
        )
      },
      {
        key: PROCESS_TYPES.MODIFICATION,
        button: (
          <Button
            className="m-r-12"
            prefixIcon={<Icon.System.Edit />}
            disabled={isLoading === PROCESS_TYPES.MODIFICATION}
            isLoading={isLoading === PROCESS_TYPES.MODIFICATION}
            onClick={() => onClick(PROCESS_TYPES.MODIFICATION)}
          >
            {t('Modification')}
          </Button>
        )
      },
      {
        key: PROCESS_TYPES.EXTENSION,
        button: (
          <Button
            className="m-r-12"
            prefixIcon={<Icon.System.Time />}
            disabled={isLoading === PROCESS_TYPES.EXTENSION}
            isLoading={isLoading === PROCESS_TYPES.EXTENSION}
            onClick={() => onClick(PROCESS_TYPES.EXTENSION)}
          >
            {t('extend_the_deadline')}
          </Button>
        )
      },
      {
        key: PROCESS_TYPES.SUSPEND,
        button: (
          <Button
            variant="red"
            className="m-r-12"
            prefixIcon={<Icon.System.Cross />}
            disabled={isLoading === PROCESS_TYPES.SUSPEND}
            isLoading={isLoading === PROCESS_TYPES.SUSPEND}
            onClick={() => onClick(PROCESS_TYPES.SUSPEND)}
          >
            {t('Suspension')}
          </Button>
        )
      },
      {
        key: PROCESS_TYPES.TERMINATION,
        button: (
          <Button
            variant="red"
            className="m-r-12"
            prefixIcon={<Icon.System.Cross />}
            disabled={isLoading === PROCESS_TYPES.TERMINATION}
            isLoading={isLoading === PROCESS_TYPES.TERMINATION}
            onClick={() => onClick(PROCESS_TYPES.TERMINATION)}
          >
            {t('Termination')}
          </Button>
        )
      },
      {
        key: PROCESS_TYPES.REPORTS,
        button: (
          <Button className="m-r-12" prefixIcon={<Icon.System.Time />} onClick={onClickReports}>
            {t('send_report')}
          </Button>
        )
      }
    ],
    [isFetched]
  );

  if (!isFetched) {
    return <Spinner />;
  }

  return (
    <>
      {item.fees && <Payment id={registerId} licenseId={licenseId} feeDateEnd={feeDateEnd} />}
      {renderButtons.map(b => {
        if (!item.applicationTypes.includes(b.key)) {
          return null;
        }
        return b.button;
      })}
      {item.branch && (
        <Button
          variant="green"
          className="m-r-12"
          prefixIcon={<Icon.System.Plus />}
          disabled={isLoadingBranch}
          isLoading={isLoadingBranch}
          onClick={onClickBranch}
        >
          {t('add_branche')}
        </Button>
      )}

      {item.applicationTypes.includes('REPORTS') && (
        <>
          <Modal isOpen={showSendReportModal} close={() => setShowSendReportModal(false)} variant="white">
            <div className={classes.sendReportModal}>
              <div className={classes.sendReportModalTitle}>{t('send_report_modal_title')}</div>
              <SendReport
                registerId={registerId}
                onSuccess={() => {
                  toast.success(t('report_sent_successfully') as string);
                  setShowSendReportModal(false);
                }}
              >
                {({ isSubmitting }) => (
                  <>
                    <Fields.Textarea rows={10} required name="text" label={t('report_text_field_label')} />
                    <Fields.RegisterFileUploader
                      required
                      registerId={registerId}
                      name="reportFileId"
                      label={t('report_file_field_label')}
                    />
                    <div className={classes.sendReportModalButtons}>
                      <Button
                        size="large"
                        htmlType="button"
                        variant="grey"
                        onClick={() => setShowSendReportModal(false)}
                      >
                        {t('cancel')}
                      </Button>
                      <Button
                        size="large"
                        disabled={isSubmitting}
                        isLoading={isSubmitting}
                        htmlType="submit"
                        variant="green"
                      >
                        {t('Send')}
                      </Button>
                    </div>
                  </>
                )}
              </SendReport>
            </div>
          </Modal>
        </>
      )}
    </>
  );
};

export default DynamicProcessButtons;
